import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Hex } from 'viem';
import { Address, RANDOM_CPU_TEAM_SLOT } from '../data/address-config';
import { battlefieldInfoForDailyField } from '../data/battlefield-info';
import { DAILY_BOSS_TABLE } from '../data/daily-boss-table';
import {
  DailyEncounter,
  DailyField,
  deriveDailyEncounter,
  dayStartMs,
  encounterHash,
  loadoutMoves,
  utcDayIndex,
} from '../data/daily-encounter';
import { ChallengeService } from './challenge.service';
import { ServerService } from './server.service';
import { RequestType, DailyBoardPayload } from '../generated/server';
import { bossAttempted, bossCleared } from '../utils/daily-board';
import { visibleInterval } from '../utils/visible-interval';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** Field → the DefaultRuleset deployed alongside it (see EngineAndPeriphery.s.sol). */
const FIELD_RULESET: Record<DailyField, Hex> = {
  flux: Address.FLUX_FIELD_RULESET as Hex,
  elemental: Address.ELEMENTAL_FIELD_RULESET as Hex,
  unstable: Address.UNSTABLE_FIELD_RULESET as Hex,
};

/** Labels match app-format-segment so the card and the picker agree. */
const MODE_LABEL = ['Singles', 'Doubles'] as const;

const MON_NAME = new Map(DAILY_BOSS_TABLE.map((m) => [m.id, m.name]));

/** How far back the boss board can be paged, today included. */
export const DAILY_HISTORY_DAYS = 7;

export interface DailyBossSlot {
  readonly monId: number;
  readonly name: string;
  readonly facetId: number;
  readonly moves: readonly { readonly name: string; readonly stamina: number }[];
}

@Injectable({ providedIn: 'root' })
export class DailyChallengeService {
  private readonly challenge = inject(ChallengeService);
  private readonly server = inject(ServerService);
  private readonly destroyRef = inject(DestroyRef);

  /** Ticks once a second so the countdown reads live and the day rolls over on its own. */
  private readonly now = signal(Date.now());

  readonly day = computed(() => utcDayIndex(this.now()));
  readonly earliestDay = computed(() => this.day() - (DAILY_HISTORY_DAYS - 1));

  /** null follows today, so an untouched pager rolls over with the clock. */
  private readonly selectedDay = signal<number | null>(null);
  readonly viewDay = computed(() => {
    const picked = this.selectedDay();
    return picked === null ? this.day() : clampDay(picked, this.earliestDay(), this.day());
  });
  readonly isToday = computed(() => this.viewDay() === this.day());
  readonly canGoBack = computed(() => this.viewDay() > this.earliestDay());
  readonly canGoForward = computed(() => this.viewDay() < this.day());

  /** Follows the paged day so the preview always describes the board underneath it. */
  readonly encounter = computed<DailyEncounter>(() => deriveDailyEncounter(this.viewDay()));

  readonly field = computed(() => this.encounter().field);
  readonly fieldLabel = computed(() => battlefieldInfoForDailyField(this.field()).name);
  readonly modeLabel = computed(() => MODE_LABEL[this.encounter().battleMode]);
  readonly bossName = computed(() => MON_NAME.get(this.encounter().leadMonId) ?? '');
  readonly hash = computed(() => encounterHash(this.encounter()));

  /** The four boss slots with their resolved loadouts — drives the boss preview. */
  readonly bossSlots = computed<DailyBossSlot[]>(() => {
    const e = this.encounter();
    return e.monIndices.map((monId, i) => ({
      monId,
      name: MON_NAME.get(monId) ?? '',
      facetId: e.facetIds[i],
      moves: loadoutMoves(monId, e.moveSelections[i]).map((m) => ({ name: m.name, stamina: m.stamina })),
    }));
  });

  /** hh:mm:ss until the next UTC midnight. */
  readonly countdown = computed(() => {
    const msLeft = Math.max(0, dayStartMs(this.day() + 1) - this.now());
    const s = Math.floor(msLeft / 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(Math.floor(s / 3600))}:${pad(Math.floor(s / 60) % 60)}:${pad(s % 60)}`;
  });

  readonly starting = signal(false);

  // Cached per day so paging back and forth doesn't drop the board you just left.
  private readonly boards = signal<ReadonlyMap<number, DailyBoardPayload>>(new Map());
  private readonly inFlight = signal<ReadonlySet<number>>(new Set());

  readonly board = computed(() => this.boards().get(this.viewDay()) ?? null);
  readonly boardLoading = computed(() => this.inFlight().has(this.viewDay()));

  /** Ranked runs; non-clearers are listed unranked and carry attempts instead. */
  readonly cleared = computed(() => bossCleared(this.board()));
  readonly attempted = computed(() => bossAttempted(this.board()));

  readonly canStart = computed(() => !this.starting() && this.challenge.canStartCpuBattle());

  constructor() {
    this.destroyRef.onDestroy(visibleInterval(1000, () => this.now.set(Date.now())));

    this.server.dailyBoard.pipe(takeUntilDestroyed()).subscribe((payload) => {
      this.boards.update((m) => new Map(m).set(payload.day, payload));
      this.inFlight.update((s) => {
        const next = new Set(s);
        next.delete(payload.day);
        return next;
      });
    });
  }

  /** Page to another day; the encounter preview and the board follow it together. */
  setViewDay(day: number): void {
    const clamped = clampDay(day, this.earliestDay(), this.day());
    this.selectedDay.set(clamped === this.day() ? null : clamped);
    this.refreshBoard(clamped);
  }

  viewToday(): void {
    this.selectedDay.set(null);
  }

  /** Ask belch for a day's board. The hash rides along so a derivation drift is logged. */
  private readonly lastBoardFetch = new Map<number, number>();

  /** The board only moves when someone finishes a run, so re-entry re-uses the cache. */
  refreshBoard(day: number = this.viewDay(), maxAgeMs = 30_000): void {
    const last = this.lastBoardFetch.get(day);
    if (last !== undefined && Date.now() - last < maxAgeMs) return;
    this.lastBoardFetch.set(day, Date.now());
    this.inFlight.update((s) => new Set(s).add(day));
    this.server.queueMessage({
      type: RequestType.GET_DAILY_BOARD,
      payload: { day, encounterHash: encounterHash(deriveDailyEncounter(day)) },
    });
  }

  /**
   * Start today's boss battle. The encounter fixes the field ruleset, the format and the boss
   * loadouts, so none of them fall back to the player's own settings.
   */
  async startDailyChallenge(): Promise<void> {
    if (!this.canStart()) return;
    this.starting.set(true);
    try {
      const day = this.day();
      const e = deriveDailyEncounter(day);
      await this.challenge.startCustomCpuBattle(
        {
          monIndices: e.monIndices.map((id) => BigInt(id)),
          facetIds: [...e.facetIds],
          moveSelections: e.moveSelections,
        },
        'extra-hard',
        RANDOM_CPU_TEAM_SLOT,
        { ruleset: FIELD_RULESET[e.field], battleMode: e.battleMode, dailyDay: day },
      );
    } finally {
      this.starting.set(false);
    }
  }
}

function clampDay(day: number, earliest: number, today: number): number {
  return Math.max(earliest, Math.min(today, day));
}
